import React, { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle } from "lucide-react";
import {
  LEAD_CARD_CLASS,
  LEAD_FIELD_CLASS,
  LEAD_SUBCOPY_CLASS,
  LEAD_TRUST_CLASS,
} from "../leadMagnetStyles";
import { trackLead } from "@/lib/analytics";
import {
  FormMountMark,
  HoneypotField,
  LEAD_SUBMIT_ERROR,
  LeadFormError,
  honeypotValue,
  submitAppsContact,
} from "@/lib/hubspot";

const TOTAL_QUESTIONS = 6;

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function slugify(value) {
  return String(value)
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/** Round to nearest $10K for readable ranges, enforce floors. */
function roundCostTenK(n) {
  const v = Math.max(20000, Math.round(n / 10000) * 10000);
  return v;
}

/**
 * Calibrated to match in-copy ranges (MVPs ~$50–150K, complex $150K+).
 * Produces indicative low / expected / high: not a binding quote.
 */
export function computeCostEstimate(answers) {
  const baseByScope = {
    "Lean MVP (prove the core loop)": 50_000,
    "Full v1 product (several feature pillars)": 105_000,
    "Platform (many modules, roles, or tenants)": 190_000,
    "Modernize or replace an existing app": 72_000,
  };

  const multProduct = {
    "Consumer (B2C) app": 1.0,
    "B2B / SaaS product": 1.12,
    "Internal / enterprise tool": 0.9,
    "Marketplace or multi-sided product": 1.26,
    "AI / ML is central to the product": 1.2,
  };

  const multPlatform = {
    "iOS only": 0.9,
    "Android only": 0.9,
    "Both iOS & Android": 1.34,
    "Web application (responsive)": 0.94,
    "Mobile apps + web admin or portal": 1.46,
  };

  const multIntegrations = {
    "Light (auth, email, analytics, a few REST endpoints)": 1.0,
    "Moderate (payments, CRM, maps, webhooks, third-party SDKs)": 1.1,
    "Heavy (ERP, EHR, legacy databases, many vendor systems)": 1.32,
  };

  const multDesign = {
    "We have product-ready designs (e.g. Figma)": 0.9,
    "We need UX + UI design from your team": 1.12,
    "Brand + rough wireframes only": 1.04,
  };

  const multTimeline = {
    "Urgent (launch in under ~4 months)": 1.1,
    "Steady (about 4–6 months)": 1.0,
    "Extended (6–12 months)": 0.95,
    "Flexible (quality over a fixed date)": 0.92,
  };

  const scope = answers.q3;
  const base = baseByScope[scope] ?? 85_000;

  let expected =
    base *
    (multProduct[answers.q1] ?? 1) *
    (multPlatform[answers.q2] ?? 1) *
    (multIntegrations[answers.q4] ?? 1) *
    (multDesign[answers.q5] ?? 1) *
    (multTimeline[answers.q6] ?? 1);

  expected = Math.min(expected, 380_000);
  expected = roundCostTenK(expected);
  const low = roundCostTenK(expected * 0.62);
  const high = roundCostTenK(expected * 1.48);

  const drivers = [];
  if ((multPlatform[answers.q2] ?? 1) >= 1.3) {
    drivers.push({ label: "Multiple surfaces (iOS + Android and/or web)", type: "neutral" });
  }
  if ((multIntegrations[answers.q4] ?? 1) >= 1.2) {
    drivers.push({ label: "Integrations and systems complexity", type: "neutral" });
  }
  if ((multDesign[answers.q5] ?? 1) > 1.05) {
    drivers.push({ label: "Full UX/UI design in scope", type: "neutral" });
  }
  if ((multTimeline[answers.q6] ?? 1) >= 1.08) {
    drivers.push({ label: "Compressed timeline (parallel workstreams)", type: "neutral" });
  }
  if ((multProduct[answers.q1] ?? 1) >= 1.15) {
    drivers.push({ label: "Marketplace, AI, or B2B multi-tenancy patterns", type: "neutral" });
  }
  if (drivers.length < 2) {
    drivers.push({ label: "QA, DevOps, and store submission / release hardening", type: "neutral" });
  }

  return {
    low,
    expected,
    high,
    drivers: drivers.slice(0, 4),
  };
}

function formatUsdRange(n) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  return `$${Math.round(n / 1000)}K`;
}

function formatCostResults(result, questions, answers) {
  const lines = [
    `Indicative range: ${formatUsdRange(result.low)} low, ${formatUsdRange(result.expected)} expected, ${formatUsdRange(result.high)} high`,
  ];
  if (result.drivers?.length) {
    lines.push(`Drivers: ${result.drivers.map((driver) => driver.label).join("; ")}`);
  }
  questions.forEach((question, index) => {
    const value = answers[question.key];
    if (value) lines.push(`Q${index + 1} ${question.headline}: ${value}`);
  });
  return lines.join("\n");
}

export default function AppCostEstimator({ page, embedded = false, variant }) {
  const effectiveVariant = variant ?? (embedded ? "embedded" : "default");
  const isModal = effectiveVariant === "modal";
  const isEmbedded = effectiveVariant === "embedded";

  const siteOrigin = "https://maxiomapps.com";
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({});
  const [capture, setCapture] = useState({
    firstName: "",
    email: "",
    company: "",
  });
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [companyUrl, setCompanyUrl] = useState("");
  const captureMountedAt = useRef(null);

  const questions = useMemo(
    () => [
      {
        key: "q1",
        eyebrow: "Question 1 of 6",
        headline: "What best describes what you're building?",
        sub: "We calibrate team size and architecture to product shape.",
        gridClass: "grid-cols-1 sm:grid-cols-2",
        options: [
          {
            value: "Consumer (B2C) app",
            icon: "📱",
            label: "Consumer (B2C)",
            desc: "End-user apps: lifestyle, fitness, social, etc.",
          },
          {
            value: "B2B / SaaS product",
            icon: "⚙️",
            label: "B2B / SaaS",
            desc: "Dashboards, tenant features, admin tooling",
          },
          {
            value: "Internal / enterprise tool",
            icon: "🏢",
            label: "Internal / enterprise",
            desc: "Employees, ops, field teams",
          },
          {
            value: "Marketplace or multi-sided product",
            icon: "🔄",
            label: "Marketplace",
            desc: "Buyers, sellers, or multi-party flows",
          },
          {
            value: "AI / ML is central to the product",
            icon: "🤖",
            label: "AI-first product",
            desc: "LLM, vision, recommendations, automation",
          },
        ],
      },
      {
        key: "q2",
        eyebrow: "Question 2 of 6",
        headline: "Which platforms do you need?",
        sub: "Each surface adds build, test, and release overhead.",
        gridClass: "grid-cols-1 sm:grid-cols-2",
        options: [
          { value: "iOS only", icon: "🍎", label: "iOS only", desc: "iPhone / iPad" },
          { value: "Android only", icon: "🤖", label: "Android only", desc: "Phones / tablets" },
          { value: "Both iOS & Android", icon: "📲", label: "iOS & Android", desc: "Both app stores" },
          { value: "Web application (responsive)", icon: "🌐", label: "Web app", desc: "Browser-first product" },
          {
            value: "Mobile apps + web admin or portal",
            icon: "🖥️",
            label: "Mobile + web",
            desc: "Apps plus web admin or customer portal",
          },
        ],
      },
      {
        key: "q3",
        eyebrow: "Question 3 of 6",
        headline: "How big is the first major release?",
        sub: "MVP vs v1 vs platform changes the engineering surface area.",
        gridClass: "grid-cols-1",
        options: [
          {
            value: "Lean MVP (prove the core loop)",
            icon: "🚀",
            label: "Lean MVP",
            desc: "One core journey, limited integrations",
          },
          {
            value: "Full v1 product (several feature pillars)",
            icon: "📦",
            label: "Full v1",
            desc: "Multiple modules and polished UX",
          },
          {
            value: "Platform (many modules, roles, or tenants)",
            icon: "🏗️",
            label: "Platform scope",
            desc: "Complex roles, workflows, or tenancy",
          },
          {
            value: "Modernize or replace an existing app",
            icon: "🔧",
            label: "Modernization",
            desc: "Rewrite, migration, or replacement",
          },
        ],
      },
      {
        key: "q4",
        eyebrow: "Question 4 of 6",
        headline: "How connected is it to other systems?",
        sub: "Integrations and data migration often drive cost more than screens.",
        gridClass: "grid-cols-1",
        options: [
          {
            value: "Light (auth, email, analytics, a few REST endpoints)",
            icon: "🔌",
            label: "Light integrations",
            desc: "Standard APIs and SaaS hooks",
          },
          {
            value: "Moderate (payments, CRM, maps, webhooks, third-party SDKs)",
            icon: "💳",
            label: "Moderate",
            desc: "Payments, CRMs, maps, notifications",
          },
          {
            value: "Heavy (ERP, EHR, legacy databases, many vendor systems)",
            icon: "🗄️",
            label: "Heavy / legacy",
            desc: "ERP, EHR, mainframes, bespoke pipes",
          },
        ],
      },
      {
        key: "q5",
        eyebrow: "Question 5 of 6",
        headline: "Where are designs and UX?",
        sub: "Discovery and product design hours scale with ambiguity.",
        gridClass: "grid-cols-1",
        options: [
          {
            value: "We have product-ready designs (e.g. Figma)",
            icon: "✨",
            label: "Designs ready",
            desc: "Engineering can start quickly",
          },
          {
            value: "We need UX + UI design from your team",
            icon: "🎨",
            label: "Need UX + UI",
            desc: "Research, flows, and visual design",
          },
          {
            value: "Brand + rough wireframes only",
            icon: "📋",
            label: "Wireframes / brand",
            desc: "Some direction, needs refinement",
          },
        ],
      },
      {
        key: "q6",
        eyebrow: "Question 6 of 6",
        headline: "What's your timeline posture?",
        sub: "Tight deadlines usually mean parallel workstreams and more senior capacity.",
        gridClass: "grid-cols-1 sm:grid-cols-2",
        options: [
          {
            value: "Urgent (launch in under ~4 months)",
            icon: "🔥",
            label: "Urgent",
            desc: "Under ~4 months target",
          },
          { value: "Steady (about 4–6 months)", icon: "📅", label: "4–6 months", desc: "Balanced pace" },
          {
            value: "Extended (6–12 months)",
            icon: "🗓️",
            label: "6–12 months",
            desc: "More room for iteration",
          },
          {
            value: "Flexible (quality over a fixed date)",
            icon: "🎯",
            label: "Flexible",
            desc: "Optimize for outcomes, not a date",
          },
        ],
      },
    ],
    []
  );

  const progressPct = useMemo(() => {
    if (step >= 1 && step <= TOTAL_QUESTIONS) return (step / TOTAL_QUESTIONS) * 100;
    if (step === TOTAL_QUESTIONS + 1) return 100;
    return 100;
  }, [step]);

  const qIndex = step - 1;
  const currentQuestion = step >= 1 && step <= TOTAL_QUESTIONS ? questions[qIndex] : null;

  const captureValid =
    capture.firstName.trim().length > 0 && isValidEmail(capture.email);

  const reset = () => {
    setStep(1);
    setAnswers({});
    setCapture({ firstName: "", email: "", company: "" });
    setResult(null);
    setSubmitting(false);
    setSubmitError("");
    setCompanyUrl("");
    captureMountedAt.current = null;
  };

  const formName = "App Cost Estimate";

  const handleCaptureSubmit = async (event) => {
    event.preventDefault();
    if (!captureValid || submitting) return;
    setSubmitError("");
    const estimate = computeCostEstimate(answers);
    setSubmitting(true);
    try {
      const outcome = await submitAppsContact(
        {
          email: capture.email,
          name: capture.firstName,
          company: capture.company,
          source: "app_cost_estimator",
          landingTitle: page?.title || "",
          landingSlug: page?.slug || "",
          results: formatCostResults(estimate, questions, answers),
        },
        {
          companyUrl: honeypotValue(event, companyUrl),
          mountedAt: captureMountedAt.current,
          name: capture.firstName,
        },
      );
      if (outcome === "sent") trackLead("app_cost_estimator");
      setResult(estimate);
      setStep(TOTAL_QUESTIONS + 2);
    } catch {
      setSubmitError(LEAD_SUBMIT_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: isModal ? 0 : isEmbedded ? 8 : 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      className={
        isModal
          ? "overflow-hidden w-full"
          : isEmbedded
            ? "overflow-hidden"
            : `${LEAD_CARD_CLASS} rounded-2xl p-6 md:p-8 overflow-hidden`
      }
      data-testid="app-cost-estimator"
    >
      <div
        className={
          isModal
            ? "h-[3px] bg-[#162033] rounded-[2px] mb-8 overflow-hidden"
            : "h-[3px] bg-[#1a1a1a] rounded-full mb-6 overflow-hidden"
        }
      >
        <div
          className="h-full bg-gradient-to-r from-[#00AEEF] to-[#06B6D4] transition-[width] duration-500 ease-out"
          style={{ width: `${progressPct}%` }}
        />
      </div>

      {currentQuestion && (
        <div>
          <p
            className={
              isModal
                ? "text-[12px] font-medium tracking-[2px] uppercase text-[#06B6D4] mb-4"
                : "text-[11px] font-medium tracking-[0.2em] uppercase text-[#06B6D4] mb-3"
            }
          >
            {currentQuestion.eyebrow}
          </p>
          <h3
            className={
              isModal
                ? "text-white font-bold text-[clamp(22px,3.6vw,30px)] leading-tight mb-2"
                : "text-xl md:text-2xl font-bold text-white leading-tight mb-2"
            }
          >
            {currentQuestion.headline}
          </h3>
          <p
            className={
              isModal
                ? "text-[#7a90b0] text-[15px] leading-relaxed mb-8"
                : `${LEAD_SUBCOPY_CLASS} leading-relaxed mb-6`
            }
          >
            {currentQuestion.sub}
          </p>

          <div className={`grid gap-3 ${currentQuestion.gridClass} mb-8`}>
            {currentQuestion.options.map((opt) => {
              const selected = answers[currentQuestion.key] === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() =>
                    setAnswers((prev) => ({ ...prev, [currentQuestion.key]: opt.value }))
                  }
                  className={
                    isModal
                      ? [
                          "bg-[#0e1825] border border-white/10 rounded-[12px] p-[18px_16px] text-left cursor-pointer transition-all duration-200 flex items-start gap-3 relative overflow-hidden select-none",
                          selected
                            ? "border-[#3b82f6] bg-[#1a2d47] shadow-[0_0_0_1px_rgba(59,130,246,0.3),0_4px_20px_rgba(59,130,246,0.15)]"
                            : "hover:border-[#00AEEF]/40 hover:translate-y-[-1px] hover:bg-[#1a2d47]",
                        ].join(" ")
                      : [
                          "bg-[#0A0A0A] border border-white/10 rounded-xl p-4 text-left transition-all duration-200 flex items-start gap-3",
                          selected
                            ? "border-[#00AEEF] ring-1 ring-[#00AEEF]/40 bg-[#00AEEF]/5"
                            : "hover:border-white/20 hover:bg-white/[0.02]",
                        ].join(" ")
                  }
                >
                  <span className="text-xl leading-none flex-shrink-0 mt-0.5">{opt.icon}</span>
                  <span className="flex-1 min-w-0">
                    <span
                      className={
                        isModal
                          ? "block font-semibold text-[14px] text-white mb-[3px]"
                          : "block font-semibold text-white text-sm"
                      }
                    >
                      {opt.label}
                    </span>
                    <span
                      className={
                        isModal
                          ? "block text-[12px] text-[#7a90b0] leading-relaxed"
                          : "block text-gray-300 text-xs mt-0.5 leading-relaxed"
                      }
                    >
                      {opt.desc}
                    </span>
                  </span>
                  <span
                    className={[
                      "w-[18px] h-[18px] rounded-full border flex-shrink-0 flex items-center justify-center mt-0.5 transition-all duration-200 ml-auto",
                      isModal ? "border-white/10" : "border-white/15",
                      selected ? "bg-[#00AEEF] border-[#00AEEF]" : "bg-transparent",
                    ].join(" ")}
                  >
                    <span
                      className={
                        selected
                          ? "text-white text-[10px] opacity-100"
                          : "text-white text-[10px] opacity-0"
                      }
                    >
                      ✓
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          <div
            className={
              step === 1
                ? isModal
                  ? "flex items-center justify-center"
                  : "flex justify-center"
                : "flex items-center justify-between gap-4"
            }
          >
            {step > 1 && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => setStep(step - 1)}
                className={
                  isModal
                    ? "bg-transparent text-[#7a90b0] hover:text-white hover:bg-transparent px-0 py-0 h-auto"
                    : "text-gray-300 hover:text-white px-0"
                }
              >
                ← Back
              </Button>
            )}
            <Button
              type="button"
              onClick={() => setStep(step + 1)}
              disabled={!answers[currentQuestion.key]}
              className={
                isModal
                  ? "bg-gradient-to-r from-[#00AEEF] to-[#2563eb] text-white font-bold px-10 py-4 rounded-[10px] shadow-[0_4px_20px_rgba(0,174,239,0.3)] disabled:opacity-40 disabled:cursor-not-allowed"
                  : "bg-[#00AEEF] hover:bg-[#0095CC] text-white font-semibold px-8 py-5 rounded-md disabled:opacity-40"
              }
            >
              Continue →
            </Button>
          </div>
        </div>
      )}

      {step === TOTAL_QUESTIONS + 1 && (
        <form
          name="app-cost-estimate"
          data-form-name={formName}
          data-hs-do-not-collect="true"
          aria-busy={submitting}
          onSubmit={handleCaptureSubmit}
        >
          <FormMountMark mountedAtRef={captureMountedAt} />
          <HoneypotField value={companyUrl} onChange={setCompanyUrl} />
          <input type="hidden" name="formName" value={formName} />
          {page?.slug ? (
            <>
              <input
                className="sr-only"
                type="text"
                name="landing_page_slug"
                value={page.slug}
                readOnly
                tabIndex={-1}
                aria-hidden="true"
              />
              <input
                className="sr-only"
                type="text"
                name="landing_page_type"
                value={page.pageType || ""}
                readOnly
                tabIndex={-1}
                aria-hidden="true"
              />
              <input
                className="sr-only"
                type="text"
                name="landing_source_path"
                value={`${siteOrigin}${page.path}`}
                readOnly
                tabIndex={-1}
                aria-hidden="true"
              />
            </>
          ) : null}
          {questions.map((q, idx) => (
            <input
              key={q.key}
              className="sr-only"
              type="text"
              name={`question_${idx + 1}_${slugify(q.headline)}`}
              value={answers[q.key] || ""}
              readOnly
            />
          ))}
          <input
            className="sr-only"
            type="text"
            name="firstName"
            value={capture.firstName || ""}
            readOnly
          />

          <p
            className={
              isModal
                ? "text-[12px] font-medium tracking-[2px] uppercase text-[#06B6D4] mb-4"
                : "text-[11px] font-medium tracking-[0.2em] uppercase text-[#06B6D4] mb-3"
            }
          >
            Almost done
          </p>
          <h3
            className={
              isModal
                ? "text-white font-bold text-[clamp(22px,3.6vw,30px)] leading-tight mb-2"
                : "text-xl md:text-2xl font-bold text-white leading-tight mb-2"
            }
          >
            Where should we send your estimate?
          </h3>
          <p
            className={
              isModal
                ? "text-[#7a90b0] text-[15px] leading-relaxed mb-8"
                : `${LEAD_SUBCOPY_CLASS} leading-relaxed mb-6`
            }
          >
            A senior engineer reviews every submission and may follow up with a clarifying question
            before you receive your written range.
          </p>

          {isModal ? (
            <div className="space-y-5 mb-8">
              <div>
                <div className="text-[13px] font-medium text-[#7a90b0] mb-2 tracking-[0.3px]">
                  Full Name
                </div>
                <Input
                  name="name"
                  value={capture.firstName}
                  onChange={(e) =>
                    setCapture((p) => ({ ...p, firstName: e.target.value }))
                  }
                  placeholder="John Smith"
                  className="w-full bg-[#0e1825] border border-white/10 rounded-[10px] px-[14px] py-[14px] text-[15px] text-white placeholder:text-[#7a90b0] focus-visible:ring-0"
                />
              </div>
              <div>
                <div className="text-[13px] font-medium text-[#7a90b0] mb-2">Work Email</div>
                <Input
                  name="email"
                  type="email"
                  value={capture.email}
                  onChange={(e) =>
                    setCapture((p) => ({ ...p, email: e.target.value }))
                  }
                  placeholder="you@company.com"
                  className="w-full bg-[#0e1825] border border-white/10 rounded-[10px] px-[14px] py-[14px] text-[15px] text-white placeholder:text-[#7a90b0] focus-visible:ring-0"
                />
              </div>
              <div>
                <div className="text-[13px] font-medium text-[#7a90b0] mb-2">
                  Company / Project Name{" "}
                  <span className="text-[#7a90b0] font-light">(optional)</span>
                </div>
                <Input
                  name="company"
                  value={capture.company}
                  onChange={(e) =>
                    setCapture((p) => ({ ...p, company: e.target.value }))
                  }
                  placeholder="Acme Corp"
                  className="w-full bg-[#0e1825] border border-white/10 rounded-[10px] px-[14px] py-[14px] text-[15px] text-white placeholder:text-[#7a90b0] focus-visible:ring-0"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4 mb-6">
              <Input
                name="name"
                value={capture.firstName}
                onChange={(e) =>
                  setCapture((p) => ({ ...p, firstName: e.target.value }))
                }
                placeholder="Full name *"
                className={LEAD_FIELD_CLASS}
              />
              <Input
                name="email"
                type="email"
                value={capture.email}
                onChange={(e) =>
                  setCapture((p) => ({ ...p, email: e.target.value }))
                }
                placeholder="Work email *"
                className={LEAD_FIELD_CLASS}
              />
              <Input
                name="company"
                value={capture.company}
                onChange={(e) =>
                  setCapture((p) => ({ ...p, company: e.target.value }))
                }
                placeholder="Company (optional)"
                className={LEAD_FIELD_CLASS}
              />
            </div>
          )}

          <LeadFormError message={submitError} testId="cost-estimator-error" />
          <Button
            type="submit"
            disabled={!captureValid || submitting}
            className={
              isModal
                ? "w-full bg-gradient-to-r from-[#00AEEF] to-[#2563eb] text-white font-bold rounded-[10px] px-8 py-5 shadow-[0_4px_20px_rgba(0,174,239,0.3)] disabled:opacity-40 disabled:cursor-not-allowed"
                : "w-full bg-[#00AEEF] hover:bg-[#0095CC] text-white font-semibold py-5 rounded-md disabled:opacity-40"
            }
          >
            {submitting ? "Sending..." : "Email my estimate →"}
          </Button>
          <p
            className={
              isModal
                ? "text-[12px] text-[#7a90b0] mt-4 leading-relaxed text-center"
                : `${LEAD_TRUST_CLASS} mt-3`
            }
          >
            Indicative range only, not a fixed quote.{" "}
            <a
              href="https://www.maxiomtech.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#06B6D4] hover:underline"
            >
              Privacy Policy
            </a>
          </p>

          <div className={isModal ? "flex justify-start mt-5" : "mt-4"}>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setStep(TOTAL_QUESTIONS)}
              className={
                isModal
                  ? "bg-transparent text-[#7a90b0] hover:text-white hover:bg-transparent px-0 py-0 h-auto"
                  : "text-gray-300 px-0"
              }
            >
              ← Back to questions
            </Button>
          </div>
        </form>
      )}

      {step === TOTAL_QUESTIONS + 2 && result && (
        <div data-testid="app-cost-estimator-results">
          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#10B981]/15 border border-[#10B981]/30 flex items-center justify-center mb-4">
              <CheckCircle className="w-7 h-7 text-[#10B981]" />
            </div>
            <h3
              className={
                isModal
                  ? "font-extrabold text-[clamp(22px,3.6vw,30px)] leading-tight text-white mb-2"
                  : "text-xl md:text-2xl font-bold text-white mb-2"
              }
            >
              Your indicative build range
            </h3>
            <p className={isModal ? "text-[16px] text-[#7a90b0] leading-relaxed max-w-[480px] mx-auto" : LEAD_SUBCOPY_CLASS}>
              We&apos;ll email the breakdown within one business day. Here&apos;s a preview.
            </p>
          </div>

          <div
            className={
              isModal
                ? "bg-[#0e1825] border border-white/10 rounded-[12px] p-7 mb-6"
                : "bg-[#0A0A0A] border border-white/10 rounded-xl p-6 mb-6"
            }
          >
            <div className="flex flex-wrap items-end justify-center gap-2 md:gap-4 mb-2">
              <div className="text-center">
                <div
                  className={
                    isModal
                      ? "text-[11px] uppercase tracking-wider text-[#7a90b0] mb-1"
                      : "text-[11px] uppercase tracking-wider text-gray-300 mb-1"
                  }
                >
                  Low
                </div>
                <div className="text-2xl md:text-3xl font-bold text-gray-300">
                  {formatUsdRange(result.low)}
                </div>
              </div>
              <div className="text-gray-600 pb-1">, </div>
              <div className="text-center">
                <div className="text-[11px] uppercase tracking-wider text-[#00AEEF] mb-1">
                  Expected
                </div>
                <div className="text-3xl md:text-4xl font-bold text-white">
                  {formatUsdRange(result.expected)}
                </div>
              </div>
              <div className="text-gray-600 pb-1">, </div>
              <div className="text-center">
                <div
                  className={
                    isModal
                      ? "text-[11px] uppercase tracking-wider text-[#7a90b0] mb-1"
                      : "text-[11px] uppercase tracking-wider text-gray-300 mb-1"
                  }
                >
                  High
                </div>
                <div className="text-2xl md:text-3xl font-bold text-gray-300">
                  {formatUsdRange(result.high)}
                </div>
              </div>
            </div>
            <p
              className={
                isModal
                  ? "text-[13px] text-[#7a90b0] text-center leading-relaxed"
                  : "text-xs text-gray-300 text-center leading-relaxed"
              }
            >
              Rounded to the nearest $10K. Final quotes follow discovery and a scoped statement of work.
            </p>
          </div>

          <ul className="space-y-2 mb-6">
            {result.drivers.map((d) => (
              <li
                key={d.label}
                className={
                  isModal
                    ? "flex items-start gap-2 text-[13px] text-[#7a90b0]"
                    : "flex items-start gap-2 text-sm text-gray-400"
                }
              >
                <span className="text-[#00AEEF] mt-1">▸</span>
                {d.label}
              </li>
            ))}
          </ul>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              asChild
              className={
                isModal
                  ? "h-12 flex-1 rounded-[10px] border-0 bg-gradient-to-r from-[#00AEEF] to-[#2563eb] px-6 text-sm font-bold text-white shadow-[0_4px_20px_rgba(0,174,239,0.3)] hover:from-[#00AEEF] hover:to-[#1d4ed8] hover:text-white"
                  : "h-12 flex-1 rounded-md border-0 bg-[#00AEEF] px-6 text-sm font-semibold text-white shadow-none hover:bg-[#0095CC] hover:text-white"
              }
            >
              <Link to="/contact">Talk to an engineer</Link>
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-12 flex-1 rounded-md border-white/20 bg-transparent px-6 text-sm font-semibold text-white shadow-none hover:bg-white/10 hover:text-white"
              onClick={reset}
            >
              Start over
            </Button>
          </div>
        </div>
      )}
    </motion.div>
  );
}
